// Branch merging: a three-way merge of node properties between the branch's starting point
// (base), the main file now, and the branch now. Changes on only one side apply cleanly; the same
// property changed differently on both sides is a conflict, resolved by a policy.

import type { DocumentJSON } from './document.ts';
import type { NodeData, NodeId, Op } from './types.ts';

export interface MergeChange {
  nodeId: NodeId;
  name: string;
  kind: 'added' | 'removed' | 'changed';
  /** changed properties (for 'changed') */
  props?: string[];
}

export interface MergeConflict {
  nodeId: NodeId;
  name: string;
  prop: string;
  main: unknown;
  branch: unknown;
}

export interface MergePlan {
  changes: MergeChange[];
  conflicts: MergeConflict[];
  /** ops that bring main up to date with the branch (conflicts resolved per policy) */
  ops: Op[];
}

const SKIP = new Set(['id']);

function index(json: DocumentJSON) {
  return new Map(json.nodes.map((n) => [n.id, n]));
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Plan merging `branch` into `main`, both descended from `base`.
 * policy 'branch': conflicting properties take the branch's value; 'main': keep main's.
 */
export function planMerge(base: DocumentJSON, main: DocumentJSON, branch: DocumentJSON, policy: 'branch' | 'main' = 'branch'): MergePlan {
  const B = index(base),
    M = index(main),
    R = index(branch);
  const changes: MergeChange[] = [];
  const conflicts: MergeConflict[] = [];
  const ops: Op[] = [];
  const created: NodeData[] = [];

  for (const [id, r] of R) {
    const b = B.get(id);
    const m = M.get(id);
    if (!b) {
      // new on the branch
      if (!m) {
        created.push(r);
        changes.push({ nodeId: id, name: label(r), kind: 'added' });
      }
      continue;
    }
    if (!m) {
      // deleted on main: the branch's edits to it can't apply
      if (!same(b, r)) conflicts.push({ nodeId: id, name: label(r), prop: '(deleted on main)', main: null, branch: 'edited' });
      continue;
    }
    const props: Record<string, unknown> = {};
    const changed: string[] = [];
    for (const k of new Set([...Object.keys(b), ...Object.keys(r)])) {
      if (SKIP.has(k)) continue;
      if (r.type === 'DOCUMENT' && k === 'name') continue; // a branch has its own file name
      if (derived(r, R, k)) continue; // layout recomputes it after the merge
      const bv = (b as unknown as Record<string, unknown>)[k];
      const rv = (r as unknown as Record<string, unknown>)[k];
      if (same(bv, rv)) continue; // the branch didn't touch it
      const mv = (m as unknown as Record<string, unknown>)[k];
      if (same(mv, rv)) continue; // already the same on main
      changed.push(k);
      if (!same(mv, bv)) {
        conflicts.push({ nodeId: id, name: label(r), prop: k, main: mv, branch: rv });
        if (policy === 'main') continue;
      }
      props[k] = rv;
    }
    if (changed.length) changes.push({ nodeId: id, name: label(r), kind: 'changed', props: changed });
    if (Object.keys(props).length) ops.push({ op: 'set', id, props } as Op);
  }

  // creations parent-first, so children land under existing parents
  const createdIds = new Set(created.map((n) => n.id));
  const placed = new Set<NodeId>();
  const order: NodeData[] = [];
  const visit = (n: NodeData) => {
    if (placed.has(n.id)) return;
    if (n.parent && createdIds.has(n.parent)) visit(R.get(n.parent)!);
    placed.add(n.id);
    order.push(n);
  };
  created.forEach(visit);
  for (const n of order) {
    // a parent deleted on main: fall back to the page it was on (or skip pages' own parents)
    let parent = n.parent;
    if (parent && !M.has(parent) && !createdIds.has(parent)) {
      conflicts.push({ nodeId: n.id, name: label(n), prop: '(parent deleted on main)', main: null, branch: parent });
      if (policy === 'main') continue;
      parent = main.nodes.find((x) => x.type === 'PAGE')?.id ?? parent;
    }
    ops.push({ op: 'create', node: { ...structuredClone(n), parent } } as Op);
  }

  // deletions: removed on the branch, untouched on main (else conflict)
  for (const [id, b] of B) {
    if (R.has(id)) continue;
    const m = M.get(id);
    if (!m) continue;
    // only delete roots of removed subtrees (children go with them)
    if (b.parent && !R.has(b.parent) && B.has(b.parent) && M.has(b.parent)) continue;
    if (!same(stripPos(m), stripPos(b))) {
      conflicts.push({ nodeId: id, name: label(m), prop: '(deleted on branch, edited on main)', main: 'edited', branch: null });
      if (policy === 'main') continue;
    }
    changes.push({ nodeId: id, name: label(b), kind: 'removed' });
    ops.push({ op: 'delete', id } as Op);
  }
  return { changes, conflicts, ops };
}

/** Properties the layout engine computes (auto-sized text, positions inside auto layout). */
function derived(n: NodeData, all: Map<NodeId, NodeData>, k: string): boolean {
  if (n.type === 'TEXT' && n.textAutoResize === 'WIDTH_AND_HEIGHT' && (k === 'width' || k === 'height')) return true;
  if (n.type === 'TEXT' && n.textAutoResize === 'HEIGHT' && k === 'height') return true;
  const parent = n.parent ? all.get(n.parent) : undefined;
  if ((k === 'x' || k === 'y') && parent?.layoutMode && parent.layoutMode !== 'NONE' && n.layoutPositioning !== 'ABSOLUTE') return true;
  return false;
}

/** How a layer is shown in the review: its text when it has some, else its name. */
function label(n: NodeData): string {
  const t = n.characters?.trim();
  if (!t) return n.name;
  // layers named after their own text (or generically) show just the text
  const generic = n.name === 'Sticky' || n.name === 'Text' || t.startsWith(n.name.trim());
  return `${generic ? '' : `${n.name}: `}${t.slice(0, 48)}`;
}

function stripPos(n: NodeData) {
  // derived layout positions shift with unrelated edits; don't count them as "edited"
  const { x: _x, y: _y, ...rest } = n;
  return rest;
}
